"use client";

import { RollbarProvider } from "./providers/rollbar";
import type {
  ErrorTrackingProvider,
  ErrorTrackingConfig,
  ErrorContext,
  ErrorTrackingUser,
} from "./types";

// Default to Rollbar but can be easily swapped
const provider: ErrorTrackingProvider = RollbarProvider;

// Error tracking API
export const errorTracking = {
  init(config?: Partial<ErrorTrackingConfig>) {
    const environment = process.env.NODE_ENV || "development";
    const codeVersion = process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA;

    const fullConfig: ErrorTrackingConfig = {
      environment,
      enabled: environment === "production" || config?.enabled === true,
      captureUnhandledRejections: true,
      codeVersion,
      ...config,
    };

    provider.init(fullConfig);

    // Expose errorTracking globally for debugging in development
    if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
      (window as any).errorTracking = errorTracking;
      console.log("Error tracking available at window.errorTracking for debugging");
    }
  },

  logError(error: Error | unknown, context?: ErrorContext) {
    console.error("Error tracked:", error, context);
    provider.logError(error, context);
  },

  logWarning(message: string, context?: ErrorContext) {
    console.warn("Warning tracked:", message, context);
    provider.logWarning(message, context);
  },

  logInfo(message: string, context?: ErrorContext) {
    console.info("Info tracked:", message, context);
    provider.logInfo(message, context);
  },

  setUser(user: ErrorTrackingUser | null) {
    provider.setUser(user);
  },

  setContext(context: ErrorContext) {
    provider.setContext(context);
  },

  clearContext() {
    provider.clearContext();
  },
};

// Export the ErrorBoundary component
export const ErrorBoundary = provider.ErrorBoundary;

// Export types
export type { ErrorTrackingUser, ErrorTrackingConfig, ErrorContext } from "./types";